import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter, takeUntil } from 'rxjs/operators';
import { GoogleAnalyticsService } from "./core/services/google-analytics.service";
import { OneIncApiService } from "./core/services/oneinc-api.service";

import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  RedirectRequest,
  EventMessage,
  EventType,
  AuthError
} from '@azure/msal-browser';
import { TokenClaims } from '@azure/msal-common';
import { Subject } from 'rxjs';
import { createClaimsTable } from "./core/utilities/claim-utils";
import { UserService } from "./core/services/user.service";
import { AuthService } from "./core/services/auth.service";
import { Routes } from "./core/constants/routes.constants";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  idleSubscription: Subscription;
  title = "Mutual of Enumclaw";
  appendedPageTitle = " | " + this.title;

  loginDisplay = false;
  isProfileLoading = false; // Add this property

  isIframe = false;  

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private router: Router,
    private titleService: Title,
    private googleAnalyticsService: GoogleAnalyticsService,
    private oneIncService: OneIncApiService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private auth: AuthService
  ) {
      this.oneIncService.initialize();
      this.googleAnalyticsService.initialize();
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.setTitle();
        this.googleAnalyticsService.addPageView(event);
      });
  }

  ngOnDestroy() {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  ngOnInit() {

      // Listen for /refresh-profile route and trigger refresh
  this.router.events.pipe(
    filter(event => event instanceof NavigationEnd)
  ).subscribe((event: NavigationEnd) => {
    if (event.urlAfterRedirects === '/refresh-profile') {
      this.refreshProfile();
    }
  });


    this.isIframe = window !== window.parent && !window.opener;
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    /**
     * You can subscribe to MSAL events as shown below. For more info,
     * visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/events.md
     */
    this.authService.instance.enableAccountStorageEvents(); // Register the storage listener that will be emitting the events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED), 
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          // Account logged out in a different tab, redirect to homepage
          window.location.pathname = "/";
        } else {
          // Update UI to show user is signed in. result.payload contains the account that was logged in
        }
      });      

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();

        this.getClaims(
          this.authService.instance.getActiveAccount()?.idTokenClaims
        );
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE), 
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (result.error instanceof AuthError) {
          // Do something with the error
        }
      });

    this.auth.isAuthenticated$.subscribe(async (isAuth) => {
      if (isAuth) {
        this.isProfileLoading = true; // Start loading
        try {
          const ok = await this.userService.loadProfileAsync();
          if (!ok) {
            console.warn('Profile load failed');
            this.auth.logout();
          }
        } catch (error) {
          console.error('Error loading profile:', error);
          this.auth.logout();
        } finally {
          this.isProfileLoading = false; // Stop loading
        }
      } else {
        // not authenticated
        this.isProfileLoading = false; // Ensure loading is stopped
        console.log('User not signed in');
      }
    });
  }

  async refreshProfile() {
    this.isProfileLoading = true;
    try {
    const ok = await this.userService.loadProfileAsync();
    if (!ok) {
      console.warn('Profile load failed');
      this.auth.logout();
    } else {
      this.router.navigate([Routes.profile.path]); // Redirect after successful refresh
    }
    } catch (error) {
      console.error('Error loading profile:', error);
      this.auth.logout();
    } finally {
      this.isProfileLoading = false;
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  getClaims(claims: any) {
    if (claims) {
      const tokenClaims = claims as TokenClaims;      
      const claimsTable = createClaimsTable(claims);
      console.log(claimsTable);
      // this.dataSource = [...claimsTable];
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginPopup({
          ...this.msalGuardConfig.authRequest,
        } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      } else {
        this.authService.loginPopup()
          .subscribe((response: AuthenticationResult) => {
            this.authService.instance.setActiveAccount(response.account);
          });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  logout() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.authService.logoutPopup({
        account: this.authService.instance.getActiveAccount(),
      });
    } else {
      this.authService.logoutRedirect({
        account: this.authService.instance.getActiveAccount(),
      });
    }
  }

  private setTitle() {
    this.titleService.setTitle(this.getRouteTitle(this.getNestedRouteTitles()));
  }

  getNestedRouteTitles(): string[] {
    let currentRoute = this.router.routerState.root.firstChild;
    const titles: string[] = [];

    while (currentRoute) {
      if (currentRoute.snapshot.routeConfig.data?.title) {
        titles.push(currentRoute.snapshot.routeConfig.data.title);
      }

      currentRoute = currentRoute.firstChild;
    }

    return titles;
  }

  private getRouteTitle(titles: string[]) {
    var lastTitle = titles.pop();
    if (lastTitle) {
      return `${lastTitle}${this.appendedPageTitle}`;
    }
    return this.title;
  }

}
